/**
 * DEV-1110 — keeps a device-authenticated session alive across a day of
 * intermittent signal.
 *
 * ── Why this is not `RefreshScheduler` ───────────────────────────────────────
 *
 * `auth/refreshScheduler.ts` runs the opposite policy, correctly, for the
 * opposite situation. It exists for a signed-in user at a desk, so when a
 * refresh fails and the token has already expired it SIGNS OUT
 * (`decideRefreshFailure`: `expiryMs == null` → sign out). Point it at a device
 * session and the first hour with no bars ends with the grower signed out of an
 * app whose entire purpose is to work with no bars. Its null-expiry branch also
 * means "refresh now", which against a rate-limited endpoint (5/min per
 * credential) would be a tight loop.
 *
 * So the policy here is inverted and deliberately dull:
 *
 *   - a failed exchange NEVER ends the session. Offline is the normal state,
 *     not an error;
 *   - retries back off (30s → 5min) instead of hammering `/public/v2/auth`;
 *   - `online` and `visibilitychange` are the real triggers. A phone that comes
 *     back into signal re-authenticates in the second it happens, not on some
 *     timer's schedule;
 *   - only an explicit `rejected` (401 — the credential was revoked in
 *     Settings, or has expired) ends it, and that is handed to the caller
 *     rather than acted on here.
 */

import {
  exchangeDeviceCredential,
  needsRenewal,
  renewDeviceCredential,
  type DeviceCredential,
} from "@/device/deviceCredential";

/** Re-exchange this long before the 12h token expires. */
export const DEVICE_TOKEN_LEAD_MS = 5 * 60 * 1000;
/** First retry delay after a failed exchange. Above the 5/min rate limit. */
export const RETRY_BASE_MS = 30 * 1000;
/** Retries back off to this, and no further. */
export const RETRY_MAX_MS = 5 * 60 * 1000;

export type DeviceSessionEvent =
  /** A live token — publish it and carry on. */
  | { type: "token"; bearer: string; expiry: number | null }
  /** No signal. The session stays usable from cache; nothing to tell the user. */
  | { type: "offline" }
  /** The credential is gone server-side. The session must end. */
  | { type: "rejected" };

export interface DeviceSessionDeps {
  getCredential: () => DeviceCredential | null;
  /** Replaces the stored credential after a renewal rotates its secret. */
  setCredential: (credential: DeviceCredential) => void;
  onEvent: (event: DeviceSessionEvent) => void;
  now?: () => number;
}

/**
 * When to act next, given the current token's expiry and how many consecutive
 * failures have happened. Pure, so the policy can be asserted without timers.
 */
export function nextAttemptDelayMs(
  nowMs: number,
  tokenExpiryMs: number | null,
  consecutiveFailures: number,
): number {
  if (consecutiveFailures > 0) {
    // 30s, 60s, 120s, 240s, 300s, 300s…
    return Math.min(RETRY_BASE_MS * 2 ** (consecutiveFailures - 1), RETRY_MAX_MS);
  }
  if (tokenExpiryMs == null) return 0;
  return Math.max(tokenExpiryMs - nowMs - DEVICE_TOKEN_LEAD_MS, 0);
}

export class DeviceSessionKeeper {
  private deps: Required<DeviceSessionDeps>;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private running = false;
  private failures = 0;
  private tokenExpiryMs: number | null = null;

  constructor(deps: DeviceSessionDeps) {
    this.deps = { now: () => Date.now(), ...deps };
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener("online", this.onBackOnline);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    void this.attempt();
  }

  stop(): void {
    this.started = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    window.removeEventListener("online", this.onBackOnline);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  /**
   * The moment signal returns, try immediately and drop the backoff — the
   * reason we were failing has just changed.
   */
  private onBackOnline = (): void => {
    this.failures = 0;
    void this.attempt();
  };

  private onVisibilityChange = (): void => {
    // A backgrounded tab's timer is throttled to the point of not firing; the
    // grower pulling the phone out of a pocket is the reliable signal.
    if (document.visibilityState === "visible") void this.attempt();
  };

  private schedule(delayMs: number): void {
    if (!this.started) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.attempt(), delayMs);
  }

  /**
   * Exchange the credential for a token if one is due, then schedule the next
   * attempt. Safe to call as often as anything likes — it no-ops while a token
   * is still fresh, which is what makes `visibilitychange` cheap.
   */
  async attempt(): Promise<void> {
    if (!this.started || this.running) return;
    const credential = this.deps.getCredential();
    if (!credential) return;

    const due = nextAttemptDelayMs(this.deps.now(), this.tokenExpiryMs, this.failures) === 0;
    if (!due && this.failures === 0) {
      this.schedule(nextAttemptDelayMs(this.deps.now(), this.tokenExpiryMs, 0));
      return;
    }

    this.running = true;
    try {
      const result = await exchangeDeviceCredential(credential);

      if (result.outcome === "rejected") {
        this.deps.onEvent({ type: "rejected" });
        this.stop();
        return;
      }
      if (result.outcome === "offline") {
        this.failures += 1;
        this.deps.onEvent({ type: "offline" });
        this.schedule(nextAttemptDelayMs(this.deps.now(), this.tokenExpiryMs, this.failures));
        return;
      }

      this.failures = 0;
      this.tokenExpiryMs = result.expiry;
      this.deps.onEvent({ type: "token", bearer: result.bearer, expiry: result.expiry });

      // We have signal AND a valid device token — the one moment the 30 days
      // can be pushed out. Best-effort: a failure here costs nothing today.
      if (needsRenewal(credential, this.deps.now())) {
        const renewed = await renewDeviceCredential(credential, result.bearer);
        if (renewed) this.deps.setCredential(renewed);
      }

      this.schedule(nextAttemptDelayMs(this.deps.now(), this.tokenExpiryMs, 0));
    } finally {
      this.running = false;
    }
  }
}
